import { price as priceUtil } from '@eti/utils';
import { LinkButton, Text, Tooltip } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import type { AgeType, CurrencyAmount, TaxCategory } from '@eti/schema-types';
import { ScreenReaderPrice } from '@eti/components';
import {
  getProductsTotalMarkup,
  getTravelerTotalMarkupFee,
  isSpecificProductsSelected,
} from '../utils';
import { CartRow, Price } from '../../CartComponents';

type ProductType = {
  travelerId: string;
  id: number | string;
  markup: number | null;
  name: string;
  title: string;
  priceRaw: number;
  weight?: number;
  weightUnit?: string;
  numberOfPieces?: number;
  piecesOfSecondBag?: number;
  weightOfSecondBag?: number;
  weightUnitOfSecondBag?: string;
  label?: string;
};

type TravelerType = {
  ageType: AgeType;
  markup: number;
  taxesAndFees: TaxCategory[];
  totalPriceSum: number;
  travelerId: string;
  vat: number;
  products: ProductType[];
};

interface TotalServiceFeeRyanairProps {
  travelers: TravelerType[];
  products: ProductType[];
  serviceFee?: CurrencyAmount;
}

const calculateTotalServiceFee = (
  travelers: TravelerType[],
  products: ProductType[],
  excludedProductsFromMarkup: String[],
  serviceFee?: CurrencyAmount,
) => {
  if (serviceFee !== undefined) {
    return serviceFee?.value || 0;
  }

  const selectedProducts = products.filter(({ id }) =>
    isSpecificProductsSelected(String(id), excludedProductsFromMarkup),
  );

  const totalProductsMarkupFee = getProductsTotalMarkup(selectedProducts);
  const totalTravelerMarkupFee = getTravelerTotalMarkupFee(travelers);

  return totalProductsMarkupFee + totalTravelerMarkupFee;
};

const TotalServiceFeeRyanair = ({
  travelers = [],
  products = [],
  serviceFee,
}: TotalServiceFeeRyanairProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();

  const excludedProductsFromMarkup = p('IbeClient.PriceBreakdown.ProductsIdsToExcludeMarkup') || [];
  const cartRowTitle = t('Cart.Row.Markup.Label');
  const tooltipContent = t('Cart.Tooltip.Markup.Label');
  const totalServiceFee = calculateTotalServiceFee(
    travelers,
    products,
    excludedProductsFromMarkup,
    serviceFee,
  );

  return (
    <>
      <CartRow data-testid="cart-total-product-markup-summary">
        <Tooltip
          content={<Text>{tooltipContent}</Text>}
          data-testid="cart-total-markup-tooltip"
          placement="top"
          title=""
          trigger="click"
        >
          <LinkButton data-testid="cart-total-markup-tooltip-link">{cartRowTitle}</LinkButton>
        </Tooltip>
        <Price aria-hidden data-testid="cart-total-product-markup-cost">
          {priceUtil.formatPrice(totalServiceFee)}
        </Price>
        <ScreenReaderPrice price={priceUtil.formatPrice(totalServiceFee)} />
      </CartRow>
    </>
  );
};

export default TotalServiceFeeRyanair;
